import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";
import { createFormatter, type Jurisdiction } from "@/lib/formatters";
import type { PolicyLoanLedgerRow, StandardLoanAmortizationRow } from "@/lib/calculationEngine";

interface CalendarYearInterest {
  year: number;
  policyLoanInterestAccrued: number;
  intendedLoanInterestPortion: number;
}

interface InvestmentInterestReportProps {
  policyLedger: PolicyLoanLedgerRow[];
  amortization: StandardLoanAmortizationRow[];
  interestBasis: 'policy' | 'intended';
  jurisdiction: Jurisdiction;
}

function aggregateByCalendarYear(
  policyLedger: PolicyLoanLedgerRow[],
  amortization: StandardLoanAmortizationRow[],
): CalendarYearInterest[] {
  const yearMap = new Map<number, { policyInterest: number; intendedInterest: number }>();

  // Policy loan: sum intervalInterestAccrued across ALL events per calendar year.
  // This captures daily accrual regardless of whether it was "paid" or capitalized,
  // giving the user the total interest cost incurred each year.
  policyLedger.forEach(e => {
    if (e.intervalInterestAccrued <= 0) return;
    const year = parseInt(e.date.slice(0, 4), 10);
    const existing = yearMap.get(year) || { policyInterest: 0, intendedInterest: 0 };
    existing.policyInterest += e.intervalInterestAccrued;
    yearMap.set(year, existing);
  });

  // Intended loan: sum interestPortion by year
  amortization.forEach(row => {
    const year = parseInt(row.paymentDate.slice(0, 4), 10);
    const existing = yearMap.get(year) || { policyInterest: 0, intendedInterest: 0 };
    existing.intendedInterest += row.interestPortion;
    yearMap.set(year, existing);
  });

  return Array.from(yearMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([year, data]) => ({
      year,
      policyLoanInterestAccrued: Math.round(data.policyInterest * 100) / 100,
      intendedLoanInterestPortion: Math.round(data.intendedInterest * 100) / 100,
    }));
}

export function InvestmentInterestReport({
  policyLedger,
  amortization,
  interestBasis,
  jurisdiction,
}: InvestmentInterestReportProps) {
  const { fmt } = createFormatter(jurisdiction);
  const yearlyData = aggregateByCalendarYear(policyLedger, amortization);

  if (yearlyData.length === 0) return null;

  const totalPolicyInterest = yearlyData.reduce((s, y) => s + y.policyLoanInterestAccrued, 0);
  const totalIntendedInterest = yearlyData.reduce((s, y) => s + y.intendedLoanInterestPortion, 0);

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base font-semibold">
          Investment Loan — Estimated Calendar-Year Interest Summary
        </CardTitle>
        <p className="text-[10px] text-muted-foreground">
          Tracking: <span className="font-semibold">{interestBasis === 'policy' ? 'Policy Loan' : 'Intended Loan'}</span> estimated interest for tax reporting
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 text-xs font-semibold text-muted-foreground uppercase">As of Dec 31</th>
                <th className="text-right py-2 text-xs font-semibold text-muted-foreground uppercase">Est. Policy Loan Interest Accrued</th>
                <th className="text-right py-2 text-xs font-semibold text-muted-foreground uppercase">Est. Intended Loan Interest</th>
                <th className="text-right py-2 text-xs font-semibold text-muted-foreground uppercase">Estimated Amount to Track</th>
              </tr>
            </thead>
            <tbody>
              {yearlyData.map(row => {
                const tracked = interestBasis === 'policy' ? row.policyLoanInterestAccrued : row.intendedLoanInterestPortion;
                return (
                  <tr key={row.year} className="border-b border-border/50">
                    <td className="py-2 font-medium">{row.year}</td>
                    <td className="py-2 text-right tabular-nums">{fmt(row.policyLoanInterestAccrued)}</td>
                    <td className="py-2 text-right tabular-nums">{fmt(row.intendedLoanInterestPortion)}</td>
                    <td className="py-2 text-right tabular-nums font-semibold text-accent">{fmt(tracked)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-border font-semibold">
                <td className="py-2">Total</td>
                <td className="py-2 text-right tabular-nums">{fmt(totalPolicyInterest)}</td>
                <td className="py-2 text-right tabular-nums">{fmt(totalIntendedInterest)}</td>
                <td className="py-2 text-right tabular-nums text-accent">
                  {fmt(interestBasis === 'policy' ? totalPolicyInterest : totalIntendedInterest)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Disclaimer */}
        <div className="flex gap-3 p-3 rounded-lg bg-destructive/5 border border-destructive/20">
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div className="space-y-2 text-xs text-muted-foreground">
            <p className="font-semibold text-destructive">Important — Estimated Values — Verify Before Filing</p>
            <p>
              These <span className="font-semibold">estimated</span> interest amounts are for planning purposes only. It is the responsibility of the policy owner to confirm and verify:
            </p>
            <ol className="list-decimal list-inside space-y-1 pl-1">
              <li>That interest was actually paid within each calendar year (interest must be paid, not merely accrued, to be deductible).</li>
              <li>That the interest paid amount calculated here is accurate with the insurance company, as any interest rate fluctuations, policy changes, or mid-year adjustments could affect these values.</li>
            </ol>
            <p>
              {jurisdiction === 'canada'
                ? 'In Canada, a T-form (such as T2210 or equivalent) may need to be completed if you intend to deduct policy loan interest for investment purposes. Consult your tax advisor.'
                : 'Consult your tax advisor regarding the deductibility of interest on policy loans used for investment purposes.'}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
